import { useRef } from 'react';
import type { KeyboardEvent } from 'react';
import type { ErpMappingRow } from '../../../api';
import { StatusBadge } from '../../ui';
import { primaryReason, rowStatusText } from './matchingCopy';

/**
 * The store's products as a queue to work through (CO-170). One row is one
 * product: its name and SKU, then where it stands, in words and facts. The
 * arrow keys move between rows and Enter opens one; only one row is in the Tab
 * order, so a page of fifty is one stop, not fifty. A product the store was
 * asked about is dimmed ("saving"), and one that was skipped says so, so
 * neither is mistaken for work left.
 */
export function MatchQueue({
  rows,
  selectedSku,
  skipped,
  onSelect,
}: {
  rows: ErpMappingRow[];
  selectedSku: string | undefined;
  skipped: ReadonlySet<string>;
  onSelect: (row: ErpMappingRow) => void;
}) {
  const buttons = useRef(new Map<string, HTMLButtonElement>());
  const tabStop = rows.find((row) => row.sku === selectedSku)?.sku ?? rows[0]?.sku;

  function onKeyDown(event: KeyboardEvent<HTMLUListElement>) {
    const index = rows.findIndex((row) => row.sku === document.activeElement?.getAttribute('data-sku'));
    let next = -1;
    if (event.key === 'ArrowDown') next = Math.min(rows.length - 1, index + 1);
    else if (event.key === 'ArrowUp') next = Math.max(0, index - 1);
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = rows.length - 1;
    if (next < 0 || index < 0) return;
    event.preventDefault();
    buttons.current.get(rows[next]!.sku)?.focus();
  }

  return (
    <ul className="match-list" aria-label="Products" onKeyDown={onKeyDown} data-testid="match-list">
      {rows.map((row) => {
        const selected = row.sku === selectedSku;
        const waiting = row.pending?.state === 'pending';
        const lead = primaryReason(row.suggestion?.reasons);
        return (
          <li key={row.sku}>
            <button
              type="button"
              ref={(element) => {
                if (element) buttons.current.set(row.sku, element);
                else buttons.current.delete(row.sku);
              }}
              data-sku={row.sku}
              className={`match-row${selected ? ' match-row--selected' : ''}${waiting ? ' match-row--waiting' : ''}${skipped.has(row.sku) ? ' match-row--skipped' : ''}`}
              aria-current={selected ? 'true' : undefined}
              tabIndex={row.sku === tabStop ? 0 : -1}
              onClick={() => onSelect(row)}
            >
              <span className="match-row__title" title={row.name || row.sku}>
                {row.name || row.sku}
              </span>
              <span className="match-row__sku">
                <code>{row.sku}</code>
              </span>
              <span className="match-row__state">
                {row.pending && row.pending.state !== 'confirmed' ? (
                  <StatusBadge kind="matchRequest" value={row.pending.state} variant="plain" />
                ) : skipped.has(row.sku) ? (
                  <span className="match-row__lead">Skipped</span>
                ) : row.state === 'no_code' && row.suggestion && lead ? (
                  <>
                    <StatusBadge kind="matchEvidence" value={lead} label={lead === 'same_code_loose' ? 'Code, zeros differ' : undefined} variant="plain" />
                    {row.suggestion.name_check === 'differ' ? <StatusBadge kind="matchNames" value="differ" variant="plain" /> : null}
                  </>
                ) : (
                  <StatusBadge kind="matchState" value={row.state} variant="plain" />
                )}
              </span>
              <span className={`match-row__detail${row.state === 'no_code' && !row.suggestion && !row.pending ? ' muted' : ''}`}>{rowStatusText(row)}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
